/**
 * Labels and signs the Edition prints around an attestation.
 *
 * - The Greek base line is labelled by its sources, LXX, OG, or E — the
 *   ecclesiastical text, as in Job, where it is largely Theodotion placed
 *   under the asterisk — several where they agree ("E/LXX"), in the editor's
 *   order.
 * - A reference may name several segments of its verse: "Job 26:14bc".
 * - A "Sub" entry stands under Origen's asterisk (※) or obelus (÷) and prints
 *   that sign where other entries print their translator.
 *
 * The API holds the same values and rules (lemmas.base_texts,
 * lemmas.sub_verse, attestations.sign).
 */

export type BaseText = 'LXX' | 'OG' | 'E'
export const BASE_TEXTS: BaseText[] = ['LXX', 'OG', 'E']
export const DEFAULT_BASE_TEXTS: readonly BaseText[] = ['LXX']

/**
 * A lemma's sources as the Edition holds them: known labels, each once, in the
 * order given. None (or none known) is the LXX.
 */
export const baseTexts = (list: readonly string[] | null | undefined): BaseText[] => {
  const known = (list || []).filter((b, i, all): b is BaseText => BASE_TEXTS.includes(b as BaseText) && all.indexOf(b) === i)
  return known.length ? known : [...DEFAULT_BASE_TEXTS]
}

/** The label of a lemma's Greek base line: "LXX", "E/LXX". */
export const baseTextsLabel = (list: readonly string[] | null | undefined): string => baseTexts(list).join('/')

/** Adds a source at the end, or takes it out; the last one stays. */
export const toggleBaseText = (list: readonly string[] | null | undefined, base: BaseText): BaseText[] => {
  const current = baseTexts(list)
  if (!current.includes(base)) return [...current, base]
  return current.length > 1 ? current.filter((b) => b !== base) : current
}

/** The segments a reference may name ("a" in Job 22:3a). */
export const SEGMENTS = ['a', 'b', 'c', 'd', 'e', 'f', 'g', 'h', 'i', 'j']

/** A reference's segments: letters in verse order, each once ("bc"). */
export const segmentsOf = (subVerse: string | null | undefined): string[] =>
  Array.from(new Set((subVerse || '').split('').filter((c) => c >= 'a' && c <= 'z'))).sort()

/** Adds a segment or takes it out; no segment left is no `sub_verse`. */
export const toggleSegment = (subVerse: string | null | undefined, letter: string): string | null => {
  const current = segmentsOf(subVerse)
  const next = current.includes(letter) ? current.filter((l) => l !== letter) : [...current, letter]
  return segmentsOf(next.join('')).join('') || null
}

/**
 * Origen's signs as they stand in a base text (Job's LXX marks its asterisked
 * passages): asterisk, obelus, metobelus. Editorial marks, not Greek — a
 * selection is never widened to take one in at its edges.
 */
export const HEXAPLARIC_SIGNS = ['※', '÷', '↙']

/**
 * The critical signs an entry can carry: Origen's asterisk (※) and obelus
 * (÷), the metobelus (↙) that closes their scope, and the lemniscus, which the
 * legacy Edition printed as "~". With `sub` the sign scopes the lemma ("Sub
 * ※"); without it the sign follows the translators ("θ′ ※").
 */
export type Sign = 'asterisk' | 'obelus' | 'metobelus' | 'lemniscus'
export const SIGNS: Sign[] = ['asterisk', 'obelus', 'metobelus', 'lemniscus']

/** The signs the wizard offers for a Sub entry. */
export const SUB_SIGNS: Sign[] = ['asterisk', 'obelus']

const SIGN_SYMBOLS: Record<Sign, string> = { asterisk: '※', obelus: '÷', metobelus: '↙', lemniscus: '~' }

/** The printed glyph of a sign; nothing for no sign. */
export const signSymbol = (sign: string | null | undefined): string =>
  (sign && SIGN_SYMBOLS[sign as Sign]) || ''

export default {
  BASE_TEXTS,
  DEFAULT_BASE_TEXTS,
  SEGMENTS,
  SIGNS,
  SUB_SIGNS,
  baseTexts,
  baseTextsLabel,
  toggleBaseText,
  segmentsOf,
  toggleSegment,
  signSymbol,
}
